import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { complianceReport } from '@mkcore/dev/schema';
import { Badge, Card, EmptyState, ErrorState, Field, Mono, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, cn } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';

const search = z.object({ days: z.coerce.number().pipe(z.union([z.literal(7), z.literal(30), z.literal(90)])).optional() });

export const Route = createFileRoute('/_app/dev/compliance')({ validateSearch: search, component: Compliance });

/**
 * The compliance panel (CO-0418): direct pushes to the default branches and pull requests merged without an
 * approval by someone else. A repository whose sync or check is not complete shows "uncertain", never a false 0.
 */
function Compliance() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const days = s.days ?? 30;
  const q = useQuery({ queryKey: ['dev', 'compliance', days], queryFn: () => call(complianceReport, { days }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  const r = q.data;
  const tile = (label: string, n: number) => {
    const tone = r?.uncertain ? 'border-warning text-warning' : n > 0 ? 'border-danger text-danger' : 'border-success text-success';
    return (
      <Card className={cn('grid gap-1 border-l-4 p-4', tone)}>
        <span className="text-sm text-fg-2">{label}</span>
        <span className="text-3xl font-bold tabular">{r?.uncertain && n === 0 ? '?' : n}</span>
        {r?.uncertain && <span className="text-xs">{t('compliance.atLeast')}</span>}
      </Card>
    );
  };
  return (
    <>
      <PageHeader eyebrow={t('nav.dev')} title={t('compliance.title')} description={t('compliance.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('activity.window')} className="w-36">
          {(p) => (
            <NativeSelect {...p} value={String(days)} onChange={(e) => void navigate({ search: { days: Number(e.target.value) as 7 | 30 | 90 }, replace: true })}>
              {[7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {t('activity.days', { count: d })}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {!r ? (
        <Skeleton className="h-64" />
      ) : !r.repositories.length ? (
        <EmptyState title={t('compliance.noRepositories')} />
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-2" data-testid="compliance-totals">
            {tile(t('compliance.directPushes'), r.totals.directPushes)}
            {tile(t('compliance.mergesWithoutReview'), r.totals.mergesWithoutReview)}
          </div>
          <h2 className="mb-2 text-lg font-bold">{t('compliance.perRepository')}</h2>
          <Table className="mb-6">
            <THead>
              <Tr>
                <Th>{t('issues.repository')}</Th>
                <Th>{t('common.status')}</Th>
                <Th className="text-right">{t('compliance.directPushes')}</Th>
                <Th className="text-right">{t('compliance.mergesWithoutReview')}</Th>
              </Tr>
            </THead>
            <TBody>
              {r.repositories.map((x) => (
                <Tr key={x.repositoryId}>
                  <Td className="font-bold">{x.fullName}</Td>
                  <Td>
                    {x.status === 'ok' ? (
                      <Badge tone="success">{t('compliance.verified')}</Badge>
                    ) : (
                      <Badge tone="warning" title={x.reason ? t(`compliance.reason.${x.reason}`, { count: x.uncheckedCommits }) : undefined}>
                        {t('compliance.uncertain')}
                      </Badge>
                    )}
                    {x.reason && <div className="text-xs text-fg-3">{t(`compliance.reason.${x.reason}`, { count: x.uncheckedCommits })}</div>}
                  </Td>
                  <Td className="text-right tabular">{x.directPushes ?? '?'}</Td>
                  <Td className="text-right tabular">{x.mergesWithoutReview ?? '?'}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          <h2 className="mb-2 text-lg font-bold">{t('compliance.findings')}</h2>
          {!r.findings.length ? (
            <p className="text-sm text-fg-3">{r.uncertain ? t('compliance.noneYet') : t('compliance.none')}</p>
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>{t('compliance.kind')}</Th>
                  <Th>{t('compliance.what')}</Th>
                  <Th className="hidden md:table-cell">{t('compliance.who')}</Th>
                  <Th className="hidden md:table-cell">{t('compliance.when')}</Th>
                </Tr>
              </THead>
              <TBody>
                {r.findings.map((f) => (
                  <Tr key={`${f.kind}-${f.repositoryId}-${f.ref}`}>
                    <Td>
                      <Badge tone="danger">{t(`compliance.${f.kind === 'direct_push' ? 'directPush' : 'mergeWithoutReview'}`)}</Badge>
                    </Td>
                    <Td>
                      <a href={f.url} target="_blank" rel="noreferrer" className="hover:underline">
                        <Mono className="text-[11px]">
                          {f.repository} {f.ref}
                        </Mono>{' '}
                        {f.title}
                      </a>
                    </Td>
                    <Td className="hidden md:table-cell">{f.actor ?? '—'}</Td>
                    <Td className="hidden md:table-cell tabular text-fg-2">{formatDateTime(f.at)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </>
      )}
    </>
  );
}
